import { Node, mergeAttributes } from '@tiptap/core';

/**
 * PiezaMultimedia — nodo de Tiptap para insertar un audio o un video dentro del
 * cuerpo de un artículo, igual que se inserta una foto.
 *
 * Se renderiza como HTML5 estándar (<figure> con <audio> o <video> y controles),
 * de modo que la versión web del artículo lo reproduce sin ningún código extra:
 * la página ya pinta el cuerpo con dangerouslySetInnerHTML.
 *
 * En el PDF impreso no hay reproductor posible, así que el backend convierte
 * este bloque en un destacado dentro del texto, con el título, la duración y el
 * enlace corto (ver LimpiarCuerpo en ExportPdfService y MULTIMEDIA.md).
 */
export const PiezaMultimedia = Node.create({
  name: 'piezaMultimedia',
  group: 'block',
  atom: true,          // se selecciona y borra como una unidad, no se edita por dentro
  draggable: true,

  addAttributes() {
    return {
      tipo:     { default: 'audio' },
      src:      { default: null },
      titulo:   { default: '' },
      autoria:  { default: '' },
      duracion: { default: '' },
      enlace:   { default: '' },
    };
  },

  parseHTML() {
    return [{
      tag: 'figure[data-pieza-multimedia]',
      getAttrs: (el) => ({
        tipo:     el.getAttribute('data-tipo') || 'audio',
        src:      el.getAttribute('data-src'),
        titulo:   el.getAttribute('data-titulo') || '',
        autoria:  el.getAttribute('data-autoria') || '',
        duracion: el.getAttribute('data-duracion') || '',
        enlace:   el.getAttribute('data-enlace') || '',
      }),
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const { tipo, src, titulo, autoria, duracion, enlace } = HTMLAttributes;
    const esVideo = tipo === 'video';
    const pie = [titulo, autoria, duracion].filter(Boolean).join(' · ');
    return [
      'figure',
      mergeAttributes({
        'data-pieza-multimedia': '',
        'data-tipo': tipo,
        'data-src': src,
        'data-titulo': titulo,
        'data-autoria': autoria,
        'data-duracion': duracion,
        'data-enlace': enlace,
        class: 'pieza-multimedia',
      }),
      [esVideo ? 'video' : 'audio', { src, controls: 'controls', preload: 'metadata',
                                      style: 'width:100%' }],
      ['figcaption', {}, pie],
    ];
  },

  // Vista dentro del editor: una tarjeta sobria en vez del reproductor, para
  // que el redactor vea qué insertó sin que se le dispare el audio al escribir.
  addNodeView() {
    return ({ node }) => {
      const { tipo, titulo, autoria, duracion } = node.attrs;
      const caja = document.createElement('div');
      caja.contentEditable = 'false';
      caja.style.cssText =
        'border:1px solid #c9a227;border-left:4px solid #c9a227;border-radius:4px;' +
        'background:#fffdf6;padding:10px 12px;margin:12px 0;font-family:inherit;';
      const etiqueta = document.createElement('div');
      etiqueta.textContent = tipo === 'video' ? '▶ VIDEO' : '♪ AUDIO';
      etiqueta.style.cssText = 'font-size:11px;font-weight:700;color:#c9a227;letter-spacing:.06em';
      const nombre = document.createElement('div');
      nombre.textContent = titulo || 'Pieza sin título';
      nombre.style.cssText = 'font-size:15px;font-weight:600;color:#1B4332;margin-top:2px';
      caja.appendChild(etiqueta);
      caja.appendChild(nombre);
      if (autoria || duracion) {
        const meta = document.createElement('div');
        meta.textContent = [autoria, duracion].filter(Boolean).join(' · ');
        meta.style.cssText = 'font-size:12px;color:#57534e;margin-top:2px';
        caja.appendChild(meta);
      }
      return { dom: caja };
    };
  },

  addCommands() {
    return {
      insertarPiezaMultimedia: (attrs) => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export default PiezaMultimedia;
